import { Component, OnInit, ViewChild } from '@angular/core';
import { routes } from '@app/consts';
import { TranslateService } from '@ngx-translate/core';
import { GridComponent, GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { GoalsReportService } from '../../services/goals-report.service';
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import { GoalsReportItem } from '../../models/goals-report.model';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { from } from 'rxjs';

@Component({
    selector: 'app-goals-details-data-grid',
    templateUrl: './goals-report-details-data-grid.component.html',
    styleUrls: ['./goals-report-details-data-grid.component.scss']
})
export class GoalsReportDetailsDataGridComponent implements OnInit {
    @ViewChild('excelexport') excelexport: any;

    public grid: GridComponent;
    public routes: typeof routes = routes;
    public bindingType = 'array';
    public gridDataResult: GridDataResult;
    pageSize: number = 20;
    skip: number = 0;
    isLoading: boolean;
    sortString: string;
    filterString: string;
    excelData: any[];
    fileName: string = 'GoalsReport.xlsx';

    filterToggleDetails: {
        text: string,
        checked: boolean
    } = {
            text: 'FilterInactive',
            checked: true
        }

    excelColumns: any[] = [
        { field: 'employeeId', title: this.translate.instant('GoalsReport-EmployeeId') },
        { field: 'employeeFirstname', title: this.translate.instant('GoalsReport-EmployeeFirstName') },
        { field: 'employeeLastname', title: this.translate.instant('GoalsReport-EmployeeLastName') },
        { field: 'managers', title: this.translate.instant('GoalsReport-Managers') },
        { field: 'goalId', title: this.translate.instant('GoalsReport-GoalId') },
        { field: 'goalTitle', title: this.translate.instant('GoalsReport-GoalTitle') },
        { field: 'goalDescription', title: this.translate.instant('GoalsReport-GoalDescription') },
        { field: 'goalExpectedCompletionDate', title: this.translate.instant('GoalsReport-GoalExpectedCompletionDate') },
        { field: 'goalActualCompletionDate', title: this.translate.instant('GoalsReport-GoalActualCompletionDate') },
        { field: 'goalWeight', title: this.translate.instant('GoalsReport-GoalWeight') },
        { field: 'parentGoal', title: this.translate.instant('GoalsReport-ParentGoal') },
        { field: 'goalPlan', title: this.translate.instant('GoalsReport-GoalPlanName') },
    ];

    public columns: any[] = [
        { field: 'employeeId', title: this.translate.instant('GoalsReport-EmployeeId'), dataType: 'String' },
        { field: 'employeeFirstname', title: this.translate.instant('GoalsReport-EmployeeFirstName'), dataType: 'String' },
        { field: 'employeeLastname', title: this.translate.instant('GoalsReport-EmployeeLastName'), dataType: 'String' },
        { field: 'managers', title: this.translate.instant('GoalsReport-Managers'), type: 'employees' },
        { field: 'goalId', title: this.translate.instant('GoalsReport-GoalId'), dataType: 'String' },
        { field: 'goalTitle', title: this.translate.instant('GoalsReport-GoalTitle'), dataType: 'String' },
        { field: 'goalDescription', title: this.translate.instant('GoalsReport-GoalDescription'), dataType: 'String' },
        { field: 'goalExpectedCompletionDate', title: this.translate.instant('GoalsReport-GoalExpectedCompletionDate'), type: 'date', dataType: 'Date' },
        { field: 'goalActualCompletionDate', title: this.translate.instant('GoalsReport-GoalActualCompletionDate'), type: 'date', dataType: 'Date' },
        { field: 'goalWeight', title: this.translate.instant('GoalsReport-GoalWeight'), type: "percentage", percentageMaxValue: 1 },
        { field: 'parentGoal', title: this.translate.instant('GoalsReport-ParentGoal'), dataType: 'String' },
        { field: 'goalPlan', title: this.translate.instant('GoalsReport-GoalPlanName'), dataType: 'String' },

    ];

    sortableColumns: any[] = [
        { field: 'employeeId', sortValue: 'employee.id' },
        { field: 'employeeFirstname', sortValue: 'employee.firstName' },
        { field: 'employeeLastname', sortValue: 'employee.lastName' },
        { field: 'managers', sortValue: 'Manager.FirstName' },
        { field: 'goalId', sortValue: 'Goal.Id' },
        // { field: 'goalTitle', sortValue: 'goal.title' },
        { field: 'goalDescription', sortValue: 'goal.description' },
        { field: 'goalExpectedCompletionDate', sortValue: 'goal.expectedCompletionDate' },
        { field: 'goalActualCompletionDate', sortValue: 'goal.actualCompletionDate' },
        { field: 'goalWeight', sortValue: 'goal.weight' },
        { field: 'parentGoal', sortValue: 'goal.parent.name' },
        { field: 'goalPlan', sortValue: 'Goal.GoalPlan.Name' },
    ];

    filterCategories: any[] = [
        { field: 'Employee.Id', title: this.translate.instant('GoalsReport-EmployeeId'), dataType: 'String' },
        { field: 'Employee.FirstName', title: this.translate.instant('GoalsReport-EmployeeFirstName'), dataType: 'String' },
        { field: 'Employee.LastName', title: this.translate.instant('GoalsReport-EmployeeLastName'), dataType: 'String' },
        { field: 'WorkLocation.Id', title: this.translate.instant('WorkLocation'), dataType: 'SpecialLookup', lookupCode: 'WORK_LOCATION' },
    ];

    constructor(
        private translate: TranslateService,
        private goalsReportService: GoalsReportService,
        private overlayService: OverlayService
    ) {

        this.getData();
    }

    ngOnInit(): void {
    }

    getData(): void {
        this.isLoading = true;
        this.goalsReportService.fetch(this.pageSize, this.skip, this.filterString, this.sortString, this.filterToggleDetails.checked)
            .subscribe((response) => {
                this.gridDataResult = {
                    data: response.data.map((item) => {
                        return {
                            employeeId: item.employee.id,
                            employeeFirstname: item.employee.firstName,
                            employeeLastname: item.employee.lastName,
                            managers: item.employee.managers,
                            goalId: item.goal.id,
                            goalTitle: item.goal.title,
                            goalDescription: item.goal.description,
                            goalExpectedCompletionDate: item.goal.expectedCompletionDate,
                            goalActualCompletionDate: item.goal.actualCompletionDate,
                            goalWeight: item.goal.weight,
                            parentGoal: item.goal.parent?.name,
                            goalPlan: item.goal.goalPlan?.name,
                        }
                    }),
                    total: response.total
                };

                this.isLoading = false;
            });
    }

    public allData() {
        this.overlayService.show('Loading');

        from(this.goalsReportService.getAllData(this.sortString, this.filterString, this.filterToggleDetails.checked))
            .subscribe({
                next: (res) => {
                    this.excelData = res.data;

                    this.excelData = this.excelData.map((item) => {
                         return {
                            employeeId: item.employee.id,
                            employeeFirstname: item.employee.firstName,
                            employeeLastname: item.employee.lastName,
                            managers: item.employee?.managers?.map(manager => `${manager.firstName} ${manager.lastName}`).join(', '),
                            goalId: item.goal.id,
                            goalTitle: item.goal.title,
                            goalDescription: item.goal.description,
                            goalExpectedCompletionDate: item.goal.expectedCompletionDate,
                            goalActualCompletionDate: item.goal.actualCompletionDate,
                            goalWeight: item.goal.weight ? Number(((parseFloat(item.goal.weight) / 1) * (100 / 1)).toFixed()) + '%' : null,
                            parentGoal: item.goal.parent?.name,
                            goalPlan: item.goal.goalPlan?.name,
                        }
                    });

                    // if I dont have this timeout first time I download the report the data will be blank
                    setTimeout(() => {
                        this.excelexport.save();
                    }, 1000);
                },
                error: (e) => {
                    this.overlayService.hide();
                },
                complete: () => this.overlayService.hide()
            });
    }

    public pageChange(event: PageChangeEvent): void {
        this.skip = event.skip;
        this.pageSize = event.take;
        this.getData();
    }

    sortChange(sortString: string) {
        if (sortString != this.sortString && this.isSortColumnSupported(sortString)) {
            this.sortString = sortString
            this.getData();
        } else {
            console.log(`Sorting by ${sortString} is not supported.`);
        }
    }

    isSortColumnSupported(sortString: string): boolean {
        const columnName = sortString.split('-')[0];
        return this.sortableColumns.some(column => column.sortValue === columnName);
    }


    updateFilterToggleDetails(toggleChange: MatSlideToggleChange) {
        this.filterToggleDetails.checked = toggleChange.checked;
        this.getData();
    }

    filterCallback(filterString: string) {
        this.filterString = filterString;
        console.log(this.filterString);
        this.getData();
    }
}
